import { X, Check } from '@phosphor-icons/react';
import { useTranslation } from '@eti/providers';
import { Card, InfoButton, Inline, RadioCard, Stack, Text, textColors, textVariants, vars } from '@etg/wings';
import { css, cx } from '@eti/styles';
import { price as priceUtils } from '@eti/utils';
import { Tier } from '@eti/schema-types';
import { getPriceDivided as getPricePerTraveler } from '../../../common/utils/price';
import { BUNDLE_PRODUCT } from '../../utils/constants';
import { screenReaderOnly } from '../../../common/utils/styles';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  formatPrice
} = priceUtils;
const wrapperStyles = isRecommended => css`
  order: ${isRecommended ? '-1' : '1'};
  position: relative;
`;
const recommendedBadgeStyles = css`
  background-color: ${vars.colors.promotional.foreground};
  border-radius: ${vars.card.borderRadius} ${vars.card.borderRadius} 0 0;
  color: #fff;
  display: flex;
  flex: 1;
  justify-content: flex-start;
  min-height: 34px;
  padding: 7px 16px 5px;
`;
const nameWrapperStyles = css`
  flex: 1;
`;
const infoIconStyles = css`
  inset-block-start: 16px;
  inset-inline-end: 16px;
  position: absolute;
`;
const headerStyles = css`
  padding-inline-end: 20px;
`;
const footerStyles = css`
  justify-content: space-between;
`;
const recommendedCardStyles = css`
  border-top: none;
  border-top-left-radius: unset;
  border-top-right-radius: unset;
`;
const lineStrikeStyle = css`
  text-decoration: line-through;
`;
const isFeatureInOption = (name, option) => Boolean(option.features.find(_ref => {
  let {
    name: optionFeatureName
  } = _ref;
  return optionFeatureName === name;
}));
const MobileOption = _ref2 => {
  let {
    handleModalOpen,
    isOpen,
    name: bundleName,
    numberOfTravelers,
    option,
    priceText,
    shouldShowPricePerPerson,
    uniqFeatures
  } = _ref2;
  const {
    t
  } = useTranslation();
  const {
    id,
    originalPrice,
    priceRaw,
    title,
    type,
    features
  } = option;
  const formattedOriginalPrice = shouldShowPricePerPerson ? formatPrice(getPricePerTraveler(originalPrice, numberOfTravelers)) : formatPrice(originalPrice);
  const formattedDiscountPrice = shouldShowPricePerPerson ? formatPrice(getPricePerTraveler(priceRaw, numberOfTravelers)) : formatPrice(priceRaw);
  const includedProducts = features.map(feature => `${feature.name}`).join(', ');
  const isRecommended = type === Tier.Recommended;
  const optionTestId = `${BUNDLE_PRODUCT}-option-${id}`;
  return /*#__PURE__*/_jsxs("div", {
    className: wrapperStyles(isRecommended),
    "data-testid": optionTestId,
    children: [isRecommended && /*#__PURE__*/_jsx(Text, {
      className: recommendedBadgeStyles,
      "data-testid": `${BUNDLE_PRODUCT}-recommended-badge`,
      id: `promotional-text-${id}`,
      variant: "14-bold",
      children: t('Product.Bundle.Recommended.Text')
    }), /*#__PURE__*/_jsxs(RadioCard, {
      "aria-describedby": `promotional-text-${id} included-products-content-${id} option-${id}-price-content`,
      className: cx({
        [recommendedCardStyles]: isRecommended
      }),
      "data-testid": `${BUNDLE_PRODUCT}-radio-input-${id}`,
      id: `choice.${bundleName}.${id}`,
      label: /*#__PURE__*/_jsx(Text, {
        className: headerStyles,
        variant: textVariants._18_EMPHASIZED,
        children: title
      }),
      value: id,
      children: [/*#__PURE__*/_jsx(InfoButton, {
        "aria-controls": "product-bundle-info-drawer",
        "aria-expanded": isOpen ? 'true' : 'false',
        "aria-haspopup": "dialog",
        className: infoIconStyles,
        "data-testid": `${BUNDLE_PRODUCT}-info-icon-${id}`,
        id: `${BUNDLE_PRODUCT}.${id}`,
        label: `${title}. ${t('ScreenReader.Product.Bundle.InfoButton.Mobile.Text')}`,
        onClick: () => handleModalOpen(id)
      }), /*#__PURE__*/_jsx(Card.Section, {
        children: /*#__PURE__*/_jsxs(Stack, {
          id: `included-products-content-${id}`,
          children: [/*#__PURE__*/_jsx("span", {
            "aria-live": "polite",
            className: screenReaderOnly,
            children: `${features.length} ${t('ScreenReader.Product.Bundle.Included.Text')}: ${includedProducts}`
          }), uniqFeatures.map(_ref3 => {
            let {
              name,
              productTypeId
            } = _ref3;
            const match = isFeatureInOption(name, option);
            return /*#__PURE__*/_jsxs(Inline, {
              align: "start",
              "aria-hidden": "true",
              as: "span",
              "data-offer-rate": match ? productTypeId : false,
              "data-testid": `${optionTestId}-feature-${productTypeId}-included-${match}`,
              children: [match ? /*#__PURE__*/_jsx(Check, {
                "aria-hidden": "true",
                color: vars.colors.signal.positive.foreground,
                size: "16"
              }) : /*#__PURE__*/_jsx(X, {
                "aria-hidden": "true",
                color: vars.colors.text.primary,
                size: "16"
              }), /*#__PURE__*/_jsx(Inline, {
                as: "label",
                className: nameWrapperStyles,
                children: name
              })]
            }, name);
          })]
        })
      }), /*#__PURE__*/_jsx(Card.Section, {
        children: /*#__PURE__*/_jsxs(Inline, {
          className: footerStyles,
          id: `option-${id}-price-content`,
          children: [/*#__PURE__*/_jsx(Stack, {
            children: /*#__PURE__*/_jsx(Text, {
              children: priceText
            })
          }), /*#__PURE__*/_jsxs(Stack, {
            align: "end",
            spacing: 0,
            children: [/*#__PURE__*/_jsxs(Text, {
              as: "p",
              variant: textVariants._18_EMPHASIZED,
              children: [/*#__PURE__*/_jsx("span", {
                className: screenReaderOnly,
                children: `${t('ScreenReader.Product.Bundle.Discounted.Price.Text')}:
            ${priceUtils.replaceWithCurrencyName(formattedDiscountPrice)}`
              }), /*#__PURE__*/_jsx("span", {
                "aria-hidden": "true",
                children: formattedDiscountPrice
              })]
            }), originalPrice > 0 && /*#__PURE__*/_jsxs(Text, {
              as: "p",
              className: lineStrikeStyle,
              color: textColors.SECONDARY,
              children: [/*#__PURE__*/_jsx("span", {
                className: screenReaderOnly,
                children: `${t('ScreenReader.Product.Bundle.Original.Price.Text')}:
              ${priceUtils.replaceWithCurrencyName(formattedOriginalPrice)}`
              }), /*#__PURE__*/_jsx("span", {
                "aria-hidden": "true",
                children: formattedOriginalPrice
              })]
            })]
          })]
        })
      })]
    })]
  });
};
export default MobileOption;
//# sourceMappingURL=MobileOption.js.map